import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import { ChangeInputHandler } from '../../../../types';
import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { formatMoney } from 'app/lib/formatters';

import { ChangeStakeTypeAmount, changeStakeTypeAmount } from '../../../../actions/stakeTypes';
import { AmountInputModifier } from '../../../../constants';
import useStakeTypesDisabledTitles from '../../../../hooks/useStakeTypesDisabledTitles';
import { RecordOfStakeTypeAmountType } from '../../../../records';
import { selectAmountStakeType } from '../../../../selectors/stakeTypes';
import { selectSystemAmountModifier, selectSystemRankEntry } from '../../../../selectors/stakeTypes/system';
import AmountInput from '../../../AmountInputByAccountType';

import dictionary from './AmountInput.dictionary.json';

import styles from '../System.module.scss';

// ---

type StoreProps = {
  amount?: RecordOfStakeTypeAmountType;
  modifier: AmountInputModifier;
  minValue: number;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeStakeTypeAmount>;
};

type SystemAmountInputProps = {
  hasError: boolean;
};

const SystemAmountInput: FC<SystemAmountInputProps & DispatchProp & StoreProps> = ({
  amount,
  modifier,
  minValue,
  hasError,
  dispatch,
}) => {
  const { lang, t } = useTranslation();
  const disabledTitle = useStakeTypesDisabledTitles();
  const changeAmountInputHandler: ChangeInputHandler = useCallback(({ value, account }) => {
    if (!account) {
      console.error('SystemAmountInput -> changeAmountInputHandler -> `account` not found');
      return;
    }

    dispatch(changeStakeTypeAmount(value, account, modifier));
  }, [modifier]);

  const minValuePlaceholder = useMemo(() => {
    if (minValue > 0) {
      return formatMoney(minValue, lang);
    }

    return 0;
  }, [lang, minValue]);

  if (!amount) {
    return null;
  }

  const placeholder = minValue > 0
    ? t(dictionary.minValuePlaceholder, { minValuePlaceholder })
    : t(dictionary.amountPlaceholder);

  return (
    <AmountInput name="system_stake_type_amount"
                 error={hasError}
                 className={styles.input}
                 placeholder={placeholder}
                 account={amount.account}
                 value={amount.value}
                 disabledTitle={disabledTitle}
                 onChange={changeAmountInputHandler} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  amount: selectAmountStakeType(state),
  modifier: selectSystemAmountModifier(state),
  minValue: selectSystemRankEntry(state)?.min || 0,
});

export default connect(mapStateToProps)(SystemAmountInput);
